/**
 * Auth context med dual-source authentication:
 *
 *   1. Supabase localStorage-session (vanlig studio-magic-link login)
 *   2. SSO via th_sso-cookie satt av hq/portal (cross-subdomain SSO)
 *
 * Vid mount probar vi forst Supabase, sedan /api/me. Om antingen ger user
 * → vi ar inloggad. Om ingen ger nagot → /login.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export type SsoUser = {
  id: string;
  email: string | null;
  source: 'bearer' | 'cookie';
};

type AuthState = {
  /** Supabase-Session om vi loggade in via studios egen magic-link, annars null. */
  session: Session | null;
  /** User-objekt fran nagon av kallorna. */
  user: User | SsoUser | null;
  /** Om vi loggades in via th_sso-cookien istallet for studios egen Supabase-session. */
  ssoOnly: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

async function tryFetchMeViaCookie(): Promise<SsoUser | null> {
  try {
    const res = await fetch(`${API_BASE}/me`, { credentials: 'include' });
    if (!res.ok) return null;
    return (await res.json()) as SsoUser;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [ssoUser, setSsoUser] = useState<SsoUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    (async () => {
      // 1) Forsok Supabase-session forst (snabbaste, localStorage)
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      if (data.session) {
        setSession(data.session);
        setLoading(false);
        return;
      }
      // 2) Inget i localStorage - kolla om vi har giltig th_sso-cookie
      const sso = await tryFetchMeViaCookie();
      if (!mounted) return;
      if (sso) setSsoUser(sso);
      setLoading(false);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      // Om vi far en riktig studio-session sa overrider den SSO
      if (newSession) setSsoUser(null);
      setLoading(false);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    // SSO-cookien rensas via portal/hq-logout - studio kan inte radera den
    // (cookien har Domain=.tophost.se, men HttpOnly hindrar JS fran att rensa).
    // Pa studio-sidan klar vi bara local state och redirectar.
    setSsoUser(null);
  };

  const user: User | SsoUser | null = session?.user ?? ssoUser;
  const ssoOnly = !session && !!ssoUser;

  return (
    <AuthContext.Provider value={{ session, user, ssoOnly, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
